import type { PromotionRecord } from "./types";

/**
 * Campaign state derived ONLY from actual record fields: is_active, the
 * revision window, and remaining capacity. There is no backend "draft"
 * state and no silent transitions — paused means is_active is false,
 * and exhausted is shown only when a remaining limit is actually zero
 * (unlimited nulls never count as exhausted).
 */
export type CampaignState = "active" | "scheduled" | "ended" | "paused" | "exhausted";

export function deriveCampaignState(record: PromotionRecord, now: Date = new Date()): CampaignState {
  if (!record.is_active) return "paused";
  const config = record.current_revision.configuration;
  const nowTime = now.getTime();
  if (config.starts_at && new Date(config.starts_at).getTime() > nowTime) return "scheduled";
  if (config.ends_at && new Date(config.ends_at).getTime() <= nowTime) return "ended";
  const remainingUses = record.metrics.remaining_uses;
  // Only a known finite zero counts as exhausted. Null, missing, or malformed
  // capacity is unknown — never exhaustion, never a reason to hide the offer.
  if (typeof remainingUses === "number" && Number.isFinite(remainingUses) && remainingUses <= 0) {
    return "exhausted";
  }
  const remainingBudget = record.metrics.remaining_budget_amount;
  if (
    typeof remainingBudget === "string" &&
    remainingBudget.trim() !== "" &&
    Number.isFinite(Number(remainingBudget)) &&
    Number(remainingBudget) <= 0
  ) {
    return "exhausted";
  }
  return "active";
}

/** StatusChip-compatible status key for a derived campaign state. */
export function campaignStatusKey(state: CampaignState): string {
  switch (state) {
    case "active":
      return "active";
    case "paused":
      return "suspended";
    case "scheduled":
      return "pending";
    case "ended":
      return "expired";
    case "exhausted":
      return "completed";
  }
}
